import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import axios, { AxiosResponse } from 'axios'
import api from 'services/api'
import {
  MaterialByIdQuery,
  MaterialByIdResponse,
  MaterialColorByIdResponse,
  CenterMaterial,
  Color,
  IMaterialCorByOv
} from './productContextTypes'
import { TypesValidations } from 'constants/enums'
import { getMaterialStock } from 'services/product'
import { formatDate } from 'commons/utils/date'
import { toast } from 'react-toastify'
import { MaterialStock } from 'services/product/interface'

interface MaterialsByColor {
  cd_cor: string
  tamanhos: MaterialColorByIdResponse[]
}

type InitialState = {
  loadingProduct: boolean
  materialSelected: MaterialByIdResponse
  filter: string[]
  materialUrlSelected: string
  materialsByColor: MaterialsByColor[]
  defaultParams: MaterialByIdQuery
  centerMaterial: CenterMaterial[]
  matrixStock: MaterialStock
  loadingStock: boolean
  hasMoreColors: boolean
}
const initialState: InitialState = {
  loadingProduct: true,
  materialSelected: {} as MaterialByIdResponse,
  filter: [] as string[],
  materialUrlSelected: '',
  materialsByColor: [] as MaterialsByColor[],
  defaultParams: {} as MaterialByIdQuery,
  centerMaterial: [] as CenterMaterial[],
  matrixStock: {} as MaterialStock,
  loadingStock: true,
  hasMoreColors: true
}
const numeroCoresPorPagina = 25

const products = createSlice({
  name: 'products',
  initialState,
  reducers: {
    onRequest(state: InitialState) {
      state.loadingProduct = true
    },
    afterRequest(state: InitialState) {
      state.loadingProduct = false
    },
    setMaterialSelected(
      state: InitialState,
      action: PayloadAction<MaterialByIdResponse>
    ) {
      const cd_matr = action.payload.cd_matr
      state.materialSelected = action.payload
      state.defaultParams = { ...state.defaultParams, cd_matr: cd_matr }
      state.materialUrlSelected = action.payload.ds_matr
        .toLocaleLowerCase()
        .replace('- ', '')
        .replace(/ /g, '-')
        .replace(/(\.|\/)/g, '')
      state.centerMaterial = action.payload?.referencias?.map((item, index) => ({
        value: index.toString(),
        title: item.cd_matr_refe
      }))
    },
    updateMaterialSelected(state: InitialState, { payload }: PayloadAction<Color[]>) {
      state.materialSelected = {
        ...state.materialSelected,
        cores: [...state.materialSelected.cores, ...payload]
      }
    },
    setMaterialsByColor(state: InitialState, action: PayloadAction<MaterialsByColor[]>) {
      state.materialsByColor = action.payload
    },
    setMaterialFilter(state: InitialState, action: PayloadAction<string[]>) {
      state.filter = action.payload
    },
    setDefaultParams(state: InitialState, action: PayloadAction<MaterialByIdQuery>) {
      state.defaultParams = action.payload
    },
    incrementToDefaultParams(state: InitialState, action: PayloadAction<string>) {
      state.defaultParams = { ...state.defaultParams, cd_matr: action.payload }
    },
    setLoadingStock(state: InitialState, action: PayloadAction<boolean>) {
      state.loadingStock = action.payload
    },
    setMatrixStock(state: InitialState, action: PayloadAction<MaterialStock>) {
      state.matrixStock = action.payload
    },
    clearMatrixStock(state: InitialState) {
      state.matrixStock = {} as MaterialStock
    },
    clearMaterialSelected(state: InitialState) {
      state.materialSelected = {} as MaterialByIdResponse
      state.materialUrlSelected = ''
      state.centerMaterial = [] as CenterMaterial[]
      const param = { ...state.defaultParams }
      delete param.cd_matr
      state.defaultParams = param
      state.filter = []
      state.hasMoreColors = true
    },
    setFalseHasMore(state: InitialState) {
      state.hasMoreColors = false
    }
  }
})

export const productContext = products.reducer
export const {
  clearMaterialSelected,
  setMaterialSelected,
  onRequest,
  setMaterialsByColor,
  setMaterialFilter,
  setDefaultParams,
  incrementToDefaultParams,
  afterRequest,
  updateMaterialSelected,
  setMatrixStock,
  setLoadingStock,
  clearMatrixStock,
  setFalseHasMore
} = products.actions

export function asyncFilterByCode(code: string): AppThunk {
  return async function (dispatch, getState) {
    const { defaultParams } = getState().products
    const query = {
      ...defaultParams,
      cd_matr: code
    }
    dispatch(asyncGetMaterialById(query))
  }
}

export function asyncGetMaterialCorByOv(
  query: IMaterialCorByOv,
  errorCallback?: () => void
): AppThunk {
  return async function (dispatch) {
    dispatch(onRequest())
    api
      .get(`/produto/v2/MaterialCorByOv`, {
        params: {
          ...query,
          nr_item_orde_vend: query.nr_item_orde_vend ?? '',
          cd_cor: query.cd_cor ?? ''
        }
      })
      .then((response: AxiosResponse<MaterialByIdResponse>) => {
        const res = { ...response.data }
        const cores = res.cores.map((cor) => ({
          ...cor,
          ds_cor: cor.ds_cor.replace('.', ' '),
          closed: false,
          checked: false
        }))
        if (cores.length < numeroCoresPorPagina) {
          dispatch(setFalseHasMore())
        }

        if (Number(query.nume_pagi) === 1) {
          dispatch(
            setMaterialSelected({
              ...response.data,
              cores: cores,
              res_cent_matr: response.data.cd_cent_matr || ''
            })
          )
        } else {
          dispatch(updateMaterialSelected(cores))
        }
        dispatch(afterRequest())
      })
      .catch(() => errorCallback && errorCallback())
  }
}

export function asyncGetMaterialById(
  query: MaterialByIdQuery,
  numeroPagina = '1',
  signal?: AbortSignal
): AppThunk {
  return async function (dispatch) {
    dispatch(onRequest())
    dispatch(getProductStock(query, signal))

    api
      .get(`/produto/v2/MaterialPedidoById`, {
        params: {
          ...query,
          nr_item_pedi_rasc: query.nr_item_pedi_rasc ?? '',
          dt_dsjd_pedi: '',
          cd_cent_matr: query?.cd_cent_matr ?? '',
          nume_pagi: numeroPagina,
          nume_regi_pagi: numeroCoresPorPagina.toString(),
          cd_cor: '',
          fl_matr_2qua: 'N'
        }
      })
      .then((response: AxiosResponse<MaterialByIdResponse>) => {
        const res = { ...response.data }
        const cores = res.cores.map((cor) => ({
          ...cor,
          ds_cor: cor.ds_cor.replace('.', ' '),
          closed: false,
          checked: false
        }))
        if (cores.length < numeroCoresPorPagina) {
          dispatch(setFalseHasMore())
        }

        if (Number(numeroPagina) === 1) {
          dispatch(
            setMaterialSelected({
              ...response.data,
              cores: cores,
              cd_cent_matr: query?.cd_cent_matr || '',
              res_cent_matr: response.data.cd_cent_matr || '',
              nr_item_pedi_rasc: query?.nr_item_pedi_rasc ?? undefined
            })
          )
        } else {
          dispatch(updateMaterialSelected(cores))
        }
      })
      .catch((err) => {
        console.error('Error =>', err)
      })
      .finally(() => {
        dispatch(afterRequest())
      })
  }
}

export const getProductStock =
  (query: MaterialByIdQuery, signal?: AbortSignal): AppThunk =>
  async (dispatch) => {
    dispatch(clearMatrixStock())
    dispatch(setLoadingStock(true))
    try {
      const payload = {
        empresa: query.cd_empr,
        organizacao: query.cd_orgn_vend,
        canal: query.cd_canl_dstb,
        setor: query.cd_seto_atvd,
        escritorio: query.cd_escr_vend,
        centro: query.cd_cent_matr,
        material: query.cd_matr,
        materialEmLinha: query.fl_matr_fora_linh === TypesValidations.NO,
        preparacaoMto: query.fl_mto_prep === TypesValidations.YES,
        dataFaturamento: formatDate(
          query.dt_dsjd_pedi,
          'YYYY-MM-DDTHH:mm:ss',
          'YYYY-MM-DD'
        )
      }

      const stockData = await getMaterialStock(payload, signal)
      if (stockData) {
        dispatch(setMatrixStock(stockData))
      }
      dispatch(setLoadingStock(false))
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error('Erro ao carregar dados do material.')
        dispatch(setLoadingStock(false))
      }
    }
  }
